import { PatternType, WeaponType, Difficulty, BossType } from '../types';

/**
 * Level System for dotDodger - All Unlocked + Boss Levels
 *
 * All levels are unlocked by default so players can jump in anywhere.
 * Levels are organized into themed categories including new boss levels
 * where powerful bosses spawn and buff all arena dots while alive.
 */

export type LevelCategory = 'main' | 'portal' | 'chaos' | 'bullet' | 'boss';

/**
 * Weapon configuration with relative spawn weight
 */
export interface WeaponConfig {
  type: WeaponType;
  weight: number;
}

/**
 * Pattern configuration with difficulty
 */
export interface PatternConfig {
  type: PatternType;
  difficulty: Difficulty;
  frequency: PatternFrequencyFunction;
}

export type PatternFrequencyFunction = (score: number) => number;

export interface RawPatternConfig {
  type: PatternType;
  difficulty: Difficulty;
  frequency: string;
}

export interface RawWeaponConfig {
  type: WeaponType;
  weight: number;
}

/**
 * Optional boss configuration for boss levels.
 * When enabled, the BossManager spawns bosses of the given types
 * on an interval, and applies buffs to all dots while the boss is alive.
 */
export interface BossLevelConfig {
  enabled: boolean;
  types: BossType[];
  spawnIntervalMs: number;
  initialDelayMs: number;
}

export interface LevelConfig {
  readonly id: string;
  readonly level: number;
  readonly name: string;
  readonly description: string;
  readonly category: LevelCategory;
  readonly categoryColor: string;

  readonly patterns: PatternConfig[];
  readonly weapons: WeaponConfig[];

  readonly patternInterval: {
    base: number;
    min: number;
  };

  readonly weaponOrbCount: number;

  readonly themeColor: string;
  readonly iconEmoji: string;

  readonly unlocks: string[];
  readonly requires?: string;

  readonly modifiers?: {
    infiniteTime?: boolean;
    scoreMultiplier?: number;
    noWeapons?: boolean;
    singleWeapon?: WeaponType;
    singlePattern?: PatternType;
  };

  readonly boss?: BossLevelConfig;
}

// Helper for default frequency
const always = (): number => 1.0;

// ============================================
// MAIN PATH
// ============================================
const LEVEL_1: LevelConfig = {
  id: 'first-steps',
  level: 1,
  name: 'First Steps',
  description: 'Learn the basics. Slow dots, simple weapons.',
  category: 'main',
  categoryColor: '#4CAF50',
  patterns: [
    { type: PatternType.ZOMBIE_SNOW, difficulty: Difficulty.EASY, frequency: always },
    { type: PatternType.CONTAINMENT_RING, difficulty: Difficulty.EASY, frequency: always },
    { type: PatternType.SPARSE_GRID, difficulty: Difficulty.EASY, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
  ],
  patternInterval: { base: 5000, min: 4000 },
  weaponOrbCount: 3,
  themeColor: '#4CAF50',
  iconEmoji: '🌱',
  unlocks: [],
  requires: undefined,
};

const LEVEL_2_MAIN: LevelConfig = {
  id: 'getting-warmer',
  level: 2,
  name: 'Getting Warmer',
  description: 'Aimed patterns and precision weapons.',
  category: 'main',
  categoryColor: '#FF9800',
  patterns: [
    { type: PatternType.ZOMBIE_SNOW, difficulty: Difficulty.EASY, frequency: always },
    { type: PatternType.SWEEPER_LINE, difficulty: Difficulty.EASY, frequency: always },
    { type: PatternType.BOUNCING_BALL, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.WAVE, difficulty: Difficulty.EASY, frequency: always },
    { type: PatternType.PULSE_RING, difficulty: Difficulty.EASY, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
    { type: WeaponType.BLASTER, weight: 1.0 },
    { type: WeaponType.HOMING_MISSILE, weight: 1.0 },
    { type: WeaponType.ICE_BOMB, weight: 1.0 },
  ],
  patternInterval: { base: 4500, min: 3500 },
  weaponOrbCount: 3,
  themeColor: '#FF9800',
  iconEmoji: '🔥',
  unlocks: [],
  requires: undefined,
};

const LEVEL_3_MAIN: LevelConfig = {
  id: 'heating-up',
  level: 3,
  name: 'Heating Up',
  description: 'Bullet hell mechanics and tactical weapons.',
  category: 'main',
  categoryColor: '#F44336',
  patterns: [
    { type: PatternType.GATLING_POINT, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.BOUNCING_BALL, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.WAVE, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.CYCLONE, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.PINWHEEL, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.HUNTER_KILLER, difficulty: Difficulty.MEDIUM, frequency: always },
  ],
  weapons: [
    { type: WeaponType.BLASTER, weight: 1.0 },
    { type: WeaponType.HOMING_MISSILE, weight: 1.0 },
    { type: WeaponType.ICE_BOMB, weight: 1.0 },
    { type: WeaponType.DOT_REPELLENT, weight: 1.0 },
    { type: WeaponType.TRIPLE_CANNON, weight: 1.0 },
  ],
  patternInterval: { base: 4000, min: 3000 },
  weaponOrbCount: 4,
  themeColor: '#F44336',
  iconEmoji: '💥',
  unlocks: [],
  requires: undefined,
};

const LEVEL_4_MAIN: LevelConfig = {
  id: 'danger-zone',
  level: 4,
  name: 'Danger Zone',
  description: 'High risk weapons. Nuclear bombs can kill YOU!',
  category: 'main',
  categoryColor: '#9C27B0',
  patterns: [
    { type: PatternType.SPIRAL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CIRCLE_BURST, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.GATLING_POINT, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CYCLONE, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CROSS_FIRE, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.PULSE_RING, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [
    { type: WeaponType.DOT_REPELLENT, weight: 1.0 },
    { type: WeaponType.TRIPLE_CANNON, weight: 1.0 },
    { type: WeaponType.NUCLEAR_BOMB, weight: 1.0 },
    { type: WeaponType.ELECTRIC_BOMB, weight: 1.0 },
    { type: WeaponType.FLAME_BURST, weight: 1.0 },
  ],
  patternInterval: { base: 3500, min: 2500 },
  weaponOrbCount: 4,
  themeColor: '#9C27B0',
  iconEmoji: '☠️',
  unlocks: [],
  requires: undefined,
};

const LEVEL_5_MAIN: LevelConfig = {
  id: 'chaos-master',
  level: 5,
  name: 'Chaos Master',
  description: 'Everything. All patterns. All weapons. Pure chaos.',
  category: 'main',
  categoryColor: '#FFD700',
  patterns: [
    { type: PatternType.SPIRAL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CYCLONE, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CIRCLE_BURST, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.GATLING_POINT, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CLOCK_SWEEP, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.AIMED_STREAMS, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.BOUNCING_BALL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.PINWHEEL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.PULSE_RING, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.HUNTER_KILLER, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CROSS_FIRE, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.BLASTER, weight: 1.0 },
    { type: WeaponType.ICE_BOMB, weight: 1.0 },
    { type: WeaponType.HOMING_MISSILE, weight: 1.0 },
    { type: WeaponType.NUCLEAR_BOMB, weight: 1.0 },
    { type: WeaponType.ELECTRIC_BOMB, weight: 1.0 },
    { type: WeaponType.DOT_REPELLENT, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
    { type: WeaponType.FLAME_BURST, weight: 1.0 },
    { type: WeaponType.TRIPLE_CANNON, weight: 1.0 },
    { type: WeaponType.FIREBALL_ORB, weight: 1.0 },
    { type: WeaponType.TESLA_CANNON, weight: 1.0 },
    { type: WeaponType.WORMHOLE, weight: 1.0 },
  ],
  patternInterval: { base: 3000, min: 2000 },
  weaponOrbCount: 5,
  themeColor: '#FFD700',
  iconEmoji: '👑',
  unlocks: [],
  requires: undefined,
};

// ============================================
// PORTAL PATH
// ============================================
const LEVEL_PORTAL_1: LevelConfig = {
  id: 'portal-novice',
  level: 1,
  name: 'Portal Novice',
  description: 'Master the wormhole. Teleport through danger.',
  category: 'portal',
  categoryColor: '#8B5CF6',
  patterns: [
    { type: PatternType.ZOMBIE_SNOW, difficulty: Difficulty.EASY, frequency: always },
    { type: PatternType.SPARSE_GRID, difficulty: Difficulty.EASY, frequency: always },
  ],
  weapons: [
    { type: WeaponType.WORMHOLE, weight: 1.0 },
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
  ],
  patternInterval: { base: 4500, min: 3500 },
  weaponOrbCount: 3,
  themeColor: '#8B5CF6',
  iconEmoji: '🌀',
  unlocks: [],
  requires: undefined,
};

const LEVEL_PORTAL_2: LevelConfig = {
  id: 'portal-adept',
  level: 2,
  name: 'Portal Adept',
  description: 'Wormholes only. No other weapons. Trust the portals.',
  category: 'portal',
  categoryColor: '#8B5CF6',
  patterns: [
    { type: PatternType.SWEEPER_LINE, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.WAVE, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.BOUNCING_BALL, difficulty: Difficulty.MEDIUM, frequency: always },
  ],
  weapons: [
    { type: WeaponType.WORMHOLE, weight: 1.0 },
  ],
  patternInterval: { base: 4000, min: 3000 },
  weaponOrbCount: 4,
  themeColor: '#A78BFA',
  iconEmoji: '🌌',
  unlocks: [],
  requires: undefined,
  modifiers: {
    singleWeapon: WeaponType.WORMHOLE,
  },
};

const LEVEL_PORTAL_3: LevelConfig = {
  id: 'portal-master',
  level: 3,
  name: 'Portal Master',
  description: 'WORMHOLES ONLY. Fast patterns. Teleport or die.',
  category: 'portal',
  categoryColor: '#8B5CF6',
  patterns: [
    { type: PatternType.SPIRAL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.AIMED_STREAMS, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.GATLING_POINT, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CYCLONE, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [
    { type: WeaponType.WORMHOLE, weight: 1.0 },
  ],
  patternInterval: { base: 3000, min: 2000 },
  weaponOrbCount: 5,
  themeColor: '#C4B5FD',
  iconEmoji: '🪐',
  unlocks: [],
  requires: undefined,
  modifiers: {
    singleWeapon: WeaponType.WORMHOLE,
    scoreMultiplier: 1.5,
  },
};

// ============================================
// CHAOS PATH
// ============================================
const LEVEL_CHAOS_1: LevelConfig = {
  id: 'nuclear-winter',
  level: 4,
  name: 'Nuclear Winter',
  description: 'Nuclear and Electric bombs ONLY. Bounce them wisely... or die.',
  category: 'chaos',
  categoryColor: '#DC2626',
  patterns: [
    { type: PatternType.BOUNCING_BALL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CYCLONE, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CIRCLE_BURST, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [
    { type: WeaponType.NUCLEAR_BOMB, weight: 1.0 },
    { type: WeaponType.ELECTRIC_BOMB, weight: 1.0 },
  ],
  patternInterval: { base: 3500, min: 2500 },
  weaponOrbCount: 4,
  themeColor: '#DC2626',
  iconEmoji: '☢️',
  unlocks: [],
  requires: undefined,
  modifiers: {
    scoreMultiplier: 1.5,
  },
};

const LEVEL_CHAOS_2: LevelConfig = {
  id: 'meltdown',
  level: 5,
  name: 'Meltdown',
  description: 'ALL bouncing bombs. Nuclear, Electric, Fireball. Chaos.',
  category: 'chaos',
  categoryColor: '#DC2626',
  patterns: [
    { type: PatternType.SPIRAL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.AIMED_STREAMS, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CLOCK_SWEEP, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CYCLONE, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [
    { type: WeaponType.NUCLEAR_BOMB, weight: 1.0 },
    { type: WeaponType.ELECTRIC_BOMB, weight: 1.0 },
    { type: WeaponType.FIREBALL_ORB, weight: 1.0 },
    { type: WeaponType.WORMHOLE, weight: 1.0 },
  ],
  patternInterval: { base: 2500, min: 1800 },
  weaponOrbCount: 5,
  themeColor: '#EF4444',
  iconEmoji: '🌋',
  unlocks: [],
  requires: undefined,
  modifiers: {
    scoreMultiplier: 2.0,
  },
};

// ============================================
// BULLET PATH
// ============================================
const LEVEL_BULLET_1: LevelConfig = {
  id: 'bullet-baby',
  level: 1,
  name: 'Bullet Baby',
  description: 'Dodge only. No weapons. Pure survival.',
  category: 'bullet',
  categoryColor: '#0EA5E9',
  patterns: [
    { type: PatternType.ZOMBIE_SNOW, difficulty: Difficulty.EASY, frequency: always },
    { type: PatternType.SPARSE_GRID, difficulty: Difficulty.EASY, frequency: always },
  ],
  weapons: [],
  patternInterval: { base: 4000, min: 3500 },
  weaponOrbCount: 0,
  themeColor: '#0EA5E9',
  iconEmoji: '🔵',
  unlocks: [],
  requires: undefined,
  modifiers: {
    noWeapons: true,
    scoreMultiplier: 1.5,
  },
};

const LEVEL_BULLET_2: LevelConfig = {
  id: 'bullet-hell',
  level: 3,
  name: 'Bullet Hell',
  description: 'All bullet patterns. No weapons. Can you survive?',
  category: 'bullet',
  categoryColor: '#0EA5E9',
  patterns: [
    { type: PatternType.GATLING_POINT, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.SPIRAL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.AIMED_STREAMS, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CIRCLE_BURST, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CLOCK_SWEEP, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [],
  patternInterval: { base: 3000, min: 2000 },
  weaponOrbCount: 0,
  themeColor: '#38BDF8',
  iconEmoji: '💫',
  unlocks: [],
  requires: undefined,
  modifiers: {
    noWeapons: true,
    scoreMultiplier: 2.0,
  },
};

const LEVEL_BULLET_3: LevelConfig = {
  id: 'bullet-storm',
  level: 5,
  name: 'Bullet Storm',
  description: 'EVERYTHING at once. No weapons. The ultimate test.',
  category: 'bullet',
  categoryColor: '#0EA5E9',
  patterns: [
    { type: PatternType.GATLING_POINT, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.SPIRAL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.AIMED_STREAMS, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CLOCK_SWEEP, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CYCLONE, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CIRCLE_BURST, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.WAVE, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.PINWHEEL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.HUNTER_KILLER, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CROSS_FIRE, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [],
  patternInterval: { base: 2000, min: 1500 },
  weaponOrbCount: 0,
  themeColor: '#7DD3FC',
  iconEmoji: '🌊',
  unlocks: [],
  requires: undefined,
  modifiers: {
    noWeapons: true,
    scoreMultiplier: 3.0,
  },
};

// ============================================
// BOSS PATH - new!
// ============================================
const LEVEL_BOSS_1: LevelConfig = {
  id: 'speed-demon',
  level: 1,
  name: 'Speed Demon',
  description: 'A roaming boss makes all dots 60% faster. Kill it!',
  category: 'boss',
  categoryColor: '#FF6B1A',
  patterns: [
    { type: PatternType.ZOMBIE_SNOW, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.SPARSE_GRID, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.CONTAINMENT_RING, difficulty: Difficulty.MEDIUM, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
    { type: WeaponType.HOMING_MISSILE, weight: 1.0 },
    { type: WeaponType.BLASTER, weight: 1.0 },
  ],
  patternInterval: { base: 4500, min: 3500 },
  weaponOrbCount: 4,
  themeColor: '#FF6B1A',
  iconEmoji: '⚡',
  unlocks: [],
  requires: undefined,
  boss: {
    enabled: true,
    types: [BossType.SPEED_DEMON],
    spawnIntervalMs: 22000,
    initialDelayMs: 8000,
  },
  modifiers: {
    scoreMultiplier: 1.5,
  },
};

const LEVEL_BOSS_2: LevelConfig = {
  id: 'shield-mother',
  level: 2,
  name: 'Shield Mother',
  description: 'Boss shields all dots from one-shot kills. Break the shield!',
  category: 'boss',
  categoryColor: '#1A8CFF',
  patterns: [
    { type: PatternType.WAVE, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.BOUNCING_BALL, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.PULSE_RING, difficulty: Difficulty.MEDIUM, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
    { type: WeaponType.ICE_BOMB, weight: 1.0 },
    { type: WeaponType.HOMING_MISSILE, weight: 1.0 },
    { type: WeaponType.NUCLEAR_BOMB, weight: 1.0 },
  ],
  patternInterval: { base: 4000, min: 3000 },
  weaponOrbCount: 4,
  themeColor: '#1A8CFF',
  iconEmoji: '🛡️',
  unlocks: [],
  requires: undefined,
  boss: {
    enabled: true,
    types: [BossType.SHIELD_MOTHER],
    spawnIntervalMs: 25000,
    initialDelayMs: 8000,
  },
  modifiers: {
    scoreMultiplier: 1.75,
  },
};

const LEVEL_BOSS_3: LevelConfig = {
  id: 'magnet-lord',
  level: 3,
  name: 'Magnet Lord',
  description: 'Boss magnetizes ALL dots toward you. Run!',
  category: 'boss',
  categoryColor: '#B026FF',
  patterns: [
    { type: PatternType.SPARSE_GRID, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.SWEEPER_LINE, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.PINWHEEL, difficulty: Difficulty.MEDIUM, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
    { type: WeaponType.BLASTER, weight: 1.0 },
    { type: WeaponType.DOT_REPELLENT, weight: 1.0 },
    { type: WeaponType.WORMHOLE, weight: 1.0 },
  ],
  patternInterval: { base: 4000, min: 3000 },
  weaponOrbCount: 4,
  themeColor: '#B026FF',
  iconEmoji: '🧲',
  unlocks: [],
  requires: undefined,
  boss: {
    enabled: true,
    types: [BossType.MAGNET_LORD],
    spawnIntervalMs: 24000,
    initialDelayMs: 7000,
  },
  modifiers: {
    scoreMultiplier: 2.0,
  },
};

const LEVEL_BOSS_4: LevelConfig = {
  id: 'splitter-king',
  level: 4,
  name: 'Splitter King',
  description: 'Killed dots split into babies while boss lives. Endless swarm!',
  category: 'boss',
  categoryColor: '#00E676',
  patterns: [
    { type: PatternType.ZOMBIE_SNOW, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.BOUNCING_BALL, difficulty: Difficulty.MEDIUM, frequency: always },
    { type: PatternType.HUNTER_KILLER, difficulty: Difficulty.MEDIUM, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
    { type: WeaponType.NUCLEAR_BOMB, weight: 1.0 },
    { type: WeaponType.ELECTRIC_BOMB, weight: 1.0 },
    { type: WeaponType.FLAME_BURST, weight: 1.0 },
  ],
  patternInterval: { base: 4000, min: 3000 },
  weaponOrbCount: 5,
  themeColor: '#00E676',
  iconEmoji: '🟢',
  unlocks: [],
  requires: undefined,
  boss: {
    enabled: true,
    types: [BossType.SPLITTER_KING],
    spawnIntervalMs: 28000,
    initialDelayMs: 6000,
  },
  modifiers: {
    scoreMultiplier: 2.0,
  },
};

const LEVEL_BOSS_5: LevelConfig = {
  id: 'boss-rush',
  level: 5,
  name: 'Boss Rush',
  description: 'ALL four bosses cycle randomly. Maximum mayhem.',
  category: 'boss',
  categoryColor: '#FF1493',
  patterns: [
    { type: PatternType.SPIRAL, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CIRCLE_BURST, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.AIMED_STREAMS, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.PULSE_RING, difficulty: Difficulty.HARD, frequency: always },
    { type: PatternType.CROSS_FIRE, difficulty: Difficulty.HARD, frequency: always },
  ],
  weapons: [
    { type: WeaponType.KINETIC_BOMB, weight: 1.0 },
    { type: WeaponType.CHAINSAW, weight: 1.0 },
    { type: WeaponType.NUCLEAR_BOMB, weight: 1.0 },
    { type: WeaponType.ELECTRIC_BOMB, weight: 1.0 },
    { type: WeaponType.FLAME_BURST, weight: 1.0 },
    { type: WeaponType.WORMHOLE, weight: 1.0 },
    { type: WeaponType.TESLA_CANNON, weight: 1.0 },
  ],
  patternInterval: { base: 3000, min: 2000 },
  weaponOrbCount: 5,
  themeColor: '#FF1493',
  iconEmoji: '👹',
  unlocks: [],
  requires: undefined,
  boss: {
    enabled: true,
    types: [
      BossType.SPEED_DEMON,
      BossType.SHIELD_MOTHER,
      BossType.MAGNET_LORD,
      BossType.SPLITTER_KING,
    ],
    spawnIntervalMs: 20000,
    initialDelayMs: 5000,
  },
  modifiers: {
    scoreMultiplier: 3.0,
  },
};

// ============================================
// ALL LEVELS
// ============================================
export const LEVELS: LevelConfig[] = [
  // Main path
  LEVEL_1,
  LEVEL_2_MAIN,
  LEVEL_3_MAIN,
  LEVEL_4_MAIN,
  LEVEL_5_MAIN,
  // Portal path
  LEVEL_PORTAL_1,
  LEVEL_PORTAL_2,
  LEVEL_PORTAL_3,
  // Chaos path
  LEVEL_CHAOS_1,
  LEVEL_CHAOS_2,
  // Bullet path
  LEVEL_BULLET_1,
  LEVEL_BULLET_2,
  LEVEL_BULLET_3,
  // Boss path
  LEVEL_BOSS_1,
  LEVEL_BOSS_2,
  LEVEL_BOSS_3,
  LEVEL_BOSS_4,
  LEVEL_BOSS_5,
];

export function getLevelById(id: string): LevelConfig | null {
  return LEVELS.find(l => l.id === id) ?? null;
}

export function getStartingLevel(): LevelConfig {
  return LEVEL_1;
}

export function getLevelsByCategory(category: LevelCategory): LevelConfig[] {
  return LEVELS.filter(l => l.category === category);
}

/**
 * ALL LEVELS UNLOCKED BY DEFAULT.
 * Returns true unconditionally so players can jump into any level.
 */
export function isLevelUnlocked(_levelId: string, _completedLevels: string[]): boolean {
  return true;
}

export function getUnlockedLevels(_completedLevels: string[]): LevelConfig[] {
  return LEVELS;
}

export function getLevelsByCategoryForUI(): Record<LevelCategory, LevelConfig[]> {
  return {
    main: getLevelsByCategory('main'),
    portal: getLevelsByCategory('portal'),
    chaos: getLevelsByCategory('chaos'),
    bullet: getLevelsByCategory('bullet'),
    boss: getLevelsByCategory('boss'),
  };
}
